import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, Loader2, Eye, Camera } from "lucide-react";

const DeptHeaderFooterUpdater = () => {
  const [deptList, setDeptList] = useState([]);
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedDeptObject, setSelectedDeptObject] = useState(null);
  const [uploadType, setUploadType] = useState("rh"); // rh, rf, seal, or watermark
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isFetchingInfo, setIsFetchingInfo] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingBranchMargin, setIsUploadingBranchMargin] = useState(false);

  const [isFetchingDepts, setIsFetchingDepts] = useState(false);

  const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
  const companyCode = localStorage.getItem("companyCode") || "-";
  const baseUrl = apiPaths[0]?.API_CONFIG || "https://cubixweberp.com:213";

  const fetchDeptList = useCallback(async () => {
    setIsFetchingDepts(true);
    try {
      const url = `${baseUrl}/api/CpaysCount/${companyCode}/DEPTLIST/-/-`;

      const response = await axios.get(url);
      if (response.data && response.data.length > 0) {
        setDeptList(response.data);
        if (!selectedDept) {
          setSelectedDept(response.data[0].deptno);
          setSelectedDeptObject(response.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching department list:", error);
    } finally {
      setIsFetchingDepts(false);
    }
  }, []);

  useEffect(() => {
    fetchDeptList();
  }, []);

  const handleFetchDeptInfo = useCallback(async () => {
    if (!selectedDept) return;
    setIsFetchingInfo(true);
    try {
      const url = `${baseUrl}/api/CpaysCount/${companyCode}/DEPTLH/${selectedDept}/-`;

      const response = await axios.get(url);
      if (response.data && response.data.length > 0) {
        setPreviewData(response.data[0]);
      } else {
        setPreviewData({ deptno: selectedDept, rh: "", rf: "", SEAL: "", watermark: "" });
      }
    } catch (error) {
      console.error("Error fetching department info:", error);
      alert("Failed to fetch department details.");
    } finally {
      setIsFetchingInfo(false);
    }
  }, [selectedDept]);

  useEffect(() => {
    if (selectedDept) handleFetchDeptInfo();
  }, [selectedDept]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedDept) {
      alert("Please select a file and department first.");
      return;
    }

    setIsUploading(true);
    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      const companyCode = localStorage.getItem("companyCode") || "-";
      const baseUrl = apiPaths[0]?.API_CONFIG || "https://cubixweberp.com:213";

      const base64String = await fileToBase64(selectedFile);
      //   const cleanBase64 = base64String.split(",")[1];

      const payload = [
        {
          CmpCode: companyCode,
          mode: "ENTRY",
          category: uploadType,
          code: selectedDept,
          imgbase64: base64String,
        },
      ];
      console.log("payload", payload);

      const url = `${baseUrl}/api/CRMimagebase64`;

      const response = await axios.post(url, payload);
      if (response.status === 200) {
        alert("Header/Footer uploaded successfully!");
        setSelectedFile(null);
        handleFetchDeptInfo();
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveBranchMarginPercentage = async () => {
    if (!selectedDeptObject) {
      alert("Please select a department first.");
      return;
    }

    // Clean and validate
    const trimmedValue = String(selectedDeptObject.pinvapr || "").trim();

    if (!trimmedValue) {
      alert("Please enter branch margin %");
      return;
    }

    // Validate it's a number
    const numValue = parseFloat(trimmedValue);
    if (isNaN(numValue)) {
      alert("Please enter a valid number");
      return;
    }

    setIsUploadingBranchMargin(true);
    try {
      const apiPaths = JSON.parse(
        localStorage.getItem("apiPaths") || "[]"
      );
      const companyCode =
        localStorage.getItem("companyCode") || "-";
      const baseUrl =
        apiPaths[0]?.API_CONFIG || "https://cubixweberp.com:213";

      console.log("apiPaths", apiPaths)

      const arrayToPass = [
        {
          compcode: companyCode,
          mod: "M", // M for modify
          deptno: selectedDeptObject.deptno,
          deptnodesc: selectedDeptObject.deptnodesc,
          listorder: selectedDeptObject.listorder || 4,
          emirates: selectedDeptObject.emirates || "",
          deptloc: selectedDeptObject.deptloc || "",
          ref: selectedDeptObject.ref || "",
          pinvapr: String(selectedDeptObject.pinvapr || "0"),
          blkdt: selectedDeptObject.blkdt || null,
          reportpath: selectedDeptObject.reportpath || "",
          enable: selectedDeptObject.enable || "",
        },
      ];

      let arraytoPassStringified = JSON.stringify(arrayToPass)


      console.log("arraytoPassStringified", arraytoPassStringified)



      const response = await axios.post(
        `${baseUrl}/api/CRMDeptReg`,
        arraytoPassStringified,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        alert("Branch Margin updated successfully!");
        fetchDeptList(); // Refresh the data
      }
    } catch (error) {
      console.error("Error updating branch margin:", error);
      alert("Failed to update Branch Margin.");
    } finally {
      setIsUploadingBranchMargin(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Controls Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Choose Department
            </label>
            <select
              value={selectedDept}
              // onChange={(e) => setSelectedDept(e.target.value)}
              onChange={(e) => {
                const selected = deptList.find(
                  (item) => item.deptno === e.target.value
                );
                setSelectedDept(e.target.value);
                setSelectedDeptObject(selected); // Save the full object
              }}
              disabled={isFetchingDepts}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-50"
            >
              {isFetchingDepts ? (
                <option>Loading departments...</option>
              ) : (
                deptList.map((item) => (
                  <option key={item.deptno} value={item.deptno}>
                    {item.deptno} - {item.deptnodesc}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="flex items-center gap-6 py-2">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="uploadType"
                value="rh"
                checked={uploadType === "rh"}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                Header
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="uploadType"
                value="rf"
                checked={uploadType === "rf"}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                Footer
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="uploadType"
                value="seal"
                checked={uploadType === "seal"}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                Seal
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="uploadType"
                value="watermark"
                checked={uploadType === "watermark"}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                Watermark
              </span>
            </label>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Upload New{" "}
              {uploadType === "rh"
                ? "Header"
                : uploadType === "rf"
                  ? "Footer"
                  : uploadType === "seal"
                    ? "Seal"
                    : "Watermark"}
            </label>
            <div
              className={`border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-blue-400 transition-all cursor-pointer bg-gray-50 ${selectedFile ? "bg-blue-50 border-blue-200" : ""
                }`}
              onClick={() =>
                document.getElementById("dept-image-input").click()
              }
            >
              <input
                id="dept-image-input"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2 text-blue-600">
                  <Camera className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {selectedFile.name}
                  </span>
                </div>
              ) : (
                <div className="space-y-1">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                  <p className="text-sm text-gray-500">Click to select image</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            {/* <button
              onClick={handleFetchDeptInfo}
              disabled={isFetchingInfo}
              className="flex-1 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 font-medium transition-all"
            >
              {isFetchingInfo ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <Eye className="w-4 h-4 text-gray-500" />
              )}
              Preview Current
            </button> */}
            <button
              onClick={handleUpload}
              disabled={isUploading || !selectedFile}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center gap-2 font-medium transition-all shadow-sm"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Upload
            </button>
          </div>

          {/* branch margin % start */}
          <div className="space-y-2 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700">
              Branch Margin %
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={selectedDeptObject?.pinvapr || ""}
                onChange={(e) => {
                  setSelectedDeptObject((prev) => ({
                    ...prev,
                    pinvapr: e.target.value,
                  }));
                }}
                onWheel={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder=""
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
              <button
                onClick={handleSaveBranchMarginPercentage}
                disabled={isUploadingBranchMargin}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300 flex items-center justify-center gap-2 font-medium transition-all shadow-sm"
              >
                {isUploadingBranchMargin ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
          {/* branch margin % end */}
        </div>

        {/* Preview Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900 border-b pb-2">
            Current View
          </h4>
          <div className="space-y-6">
            <div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Header
              </span>
              <div className="mt-2 h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                {previewData?.rh ? (
                  <img
                    src={previewData.rh}
                    alt="Header"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-gray-400 italic">
                    No header set
                  </span>
                )}
              </div>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Footer
              </span>
              <div className="mt-2 h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                {previewData?.rf ? (
                  <img
                    src={previewData.rf}
                    alt="Footer"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-gray-400 italic">
                    No footer set
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Seal
                </span>
                <div className="mt-2 h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                  {previewData?.SEAL ? (
                    <img
                      src={previewData.SEAL}
                      alt="Seal"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      No seal set
                    </span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Watermark
                </span>
                <div className="mt-2 h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                  {previewData?.watermark ? (
                    <img
                      src={previewData.watermark}
                      alt="Watermark"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      No watermark set
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeptHeaderFooterUpdater;
